import WhatsAppChat from './WhatsAppChat';

const badges = [
  { icon: 'ri-time-line', label: '24/7 Atendimento Ativo' },
  { icon: 'ri-flashlight-line', label: 'Resposta Instantânea' },
  { icon: 'ri-line-chart-line', label: '3x Mais Conversões' },
  { icon: 'ri-shield-check-line', label: 'Zero Leads Perdidos' },
];

interface HeroProps {
  onPrimaryCtaClick: () => void;
  onSecondaryCtaClick: () => void;
}

export default function Hero({ onPrimaryCtaClick, onSecondaryCtaClick }: HeroProps) {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0A1628 0%, #112448 50%, #0A1628 100%)' }}
    >
      {/* Background decorative elements */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #E94560 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-8 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #E94560 0%, transparent 70%)', transform: 'translate(-40%, 40%)' }}
      />
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 w-full pt-28 pb-20 lg:pt-32 lg:pb-24">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          {/* Left: Text content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Eyebrow */}
            <div
              className="inline-flex items-center gap-2 border border-coral/40 rounded-full px-4 py-1.5 mb-6 opacity-0 animate-fade-in"
              style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
              <span className="font-b2bbody text-coral text-xs font-semibold tracking-wider uppercase">
                Agente de IA para WhatsApp B2B
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-b2b text-[2.1rem] sm:text-[2.6rem] lg:text-[3rem] xl:text-[3.25rem] font-extrabold text-white leading-[1.15] mb-6 opacity-0 animate-fade-up"
              style={{ animationDelay: '80ms', animationFillMode: 'forwards' }}
            >
              Sua empresa fatura mais de{' '}
              <span className="text-coral">R$ 1000</span> por venda.
              <br className="hidden sm:block" />
              Quantas você perde por{' '}
              <span className="relative">
                demora
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full"
                  style={{ background: '#E94560', bottom: '-2px' }}
                />
              </span>{' '}
              no WhatsApp?
            </h1>

            {/* Sub-headline */}
            <p
              className="font-b2bbody text-white/65 text-base sm:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8 opacity-0 animate-fade-up"
              style={{ animationDelay: '180ms', animationFillMode: 'forwards' }}
            >
              A Automat treina um agente de IA no seu catálogo e coloca ele pra atender
              seus clientes no WhatsApp — qualificando leads, respondendo dúvidas técnicas
              e agendando reuniões comerciais.{' '}
              <strong className="text-white/90 font-semibold">
                24 horas por dia, 7 dias por semana.
              </strong>
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mb-10 opacity-0 animate-fade-up"
              style={{ animationDelay: '280ms', animationFillMode: 'forwards' }}
            >
              <button
                onClick={onPrimaryCtaClick}
                className="whitespace-nowrap w-full sm:w-auto font-b2bbody font-bold text-base px-7 py-3.5 rounded-xl text-white cursor-pointer transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #E94560, #C93050)' }}
              >
                Ver Demo Personalizada
                <i className="ri-arrow-right-line ml-2" />
              </button>
              <button
                onClick={onSecondaryCtaClick}
                className="whitespace-nowrap w-full sm:w-auto font-b2bbody font-semibold text-base px-7 py-3.5 rounded-xl border-2 border-white/30 text-white/80 cursor-pointer transition-all duration-200 hover:border-white/60 hover:text-white hover:bg-white/5"
              >
                Ver Como Funciona
                <i className="ri-play-circle-line ml-2" />
              </button>
            </div>

            {/* Badges */}
            <div
              className="grid grid-cols-2 gap-2.5 max-w-sm mx-auto lg:mx-0 opacity-0 animate-fade-up"
              style={{ animationDelay: '380ms', animationFillMode: 'forwards' }}
            >
              {badges.map((badge) => (
                <div
                  key={badge.label}
                  className="flex items-center gap-2 bg-white/8 border border-white/12 rounded-xl px-3.5 py-2.5"
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${badge.icon} text-coral text-base`} />
                  </div>
                  <span className="font-b2bbody text-white/80 text-xs font-medium leading-tight">
                    {badge.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: WhatsApp mockup */}
          <div
            className="flex-shrink-0 w-full lg:w-auto flex justify-center lg:justify-end opacity-0 animate-fade-in"
            style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
          >
            <div className="relative">
              {/* Glow behind card */}
              <div
                className="absolute inset-0 rounded-2xl blur-2xl opacity-25 scale-95"
                style={{ background: 'linear-gradient(135deg, #075E54, #E94560)' }}
              />
              <WhatsAppChat className="relative" />

              {/* Floating badge: "Respondido em 2s" */}
              <div
                className="absolute -left-4 bottom-24 bg-white rounded-xl px-3 py-2 shadow-lg flex items-center gap-2 opacity-0 animate-fade-in"
                style={{ animationDelay: '900ms', animationFillMode: 'forwards' }}
              >
                <i className="ri-flashlight-fill text-coral text-sm" />
                <span className="font-b2bbody text-navy text-xs font-bold">Respondido em 2s</span>
              </div>

              {/* Floating badge: 24/7 */}
              <div
                className="absolute -right-4 top-20 bg-coral rounded-xl px-3 py-2 shadow-lg flex items-center gap-2 opacity-0 animate-fade-in"
                style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}
              >
                <i className="ri-moon-line text-white text-sm" />
                <span className="font-b2bbody text-white text-xs font-bold">Ativo 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #0A1628)' }}
      />
    </section>
  );
}